import React from 'react';
import { Box, Typography, Button, formatPrice } from '@lucid/lego';
import type { CheckoutPricing } from '../types';
import styles from './CartToolbar.module.scss';

type CartToolbarLabels = {
  cart?: string;
  deleteCart?: string;
  deleting?: string;
  configureNewVehicle?: string;
  addAvailableVehicle?: string;
};

export type CartToolbarProps = {
  theme: 'dark' | 'light';
  invertedTheme: 'dark' | 'light';
  labels?: CartToolbarLabels;
  totalVehicles: number;
  pricing?: CheckoutPricing;
  locale?: string;
  onDeleteCart: () => void;
  isDeletingCart: boolean;
  onConfigureNewVehicle: () => void;
  onAddAvailableVehicle: () => void;
};

const formatDisplayPrice = (amount: number, locale: string): string =>
  formatPrice(amount, { locale });

/**
 * Toolbar showing cart summary info and quick-action buttons for managing the cart.
 * @component
 * @param {CartToolbarProps} props
 * @returns {JSX.Element}
 * @example
 * <CartToolbar theme="light" invertedTheme="dark" totalVehicles={3} isDeletingCart={false} onDeleteCart={() => {}} onConfigureNewVehicle={() => {}} onAddAvailableVehicle={() => {}} />
 */
const CartToolbar: React.FC<CartToolbarProps> = ({
  theme,
  invertedTheme,
  labels,
  totalVehicles,
  pricing,
  locale,
  onDeleteCart,
  isDeletingCart,
  onConfigureNewVehicle,
  onAddAvailableVehicle,
}) => (
  <Box
    className={styles.cartToolbar}
    background="white"
    pl={16}
    pr={16}
    pt={24}
    pb={24}
    data-testid="checkout-cart-toolbar"
  >
    <Typography variant="body" size="medium" color="black" theme={theme}>
      {labels?.cart ?? 'Cart'}&nbsp;
      <Typography variant="body" size="medium" color="gray-5" theme={theme} as="span">
        {totalVehicles} {totalVehicles === 1 ? 'vehicle' : 'vehicles'}
      </Typography>
      {pricing && (
        <>
          &nbsp;•&nbsp;
          <Typography variant="body" size="medium" color="gray-5" theme={theme} as="span">
            {formatDisplayPrice(pricing.subtotal ?? 0, locale ?? 'de-de')} Total
          </Typography>
        </>
      )}
    </Typography>
    <Box className={styles.cartToolbarActions}>
      <Button
        theme={invertedTheme}
        size="small"
        type="secondary"
        color="neutral"
        dataTestId="delete-cart-button"
        gaComponent="order-builder-checkout"
        onClick={onDeleteCart}
        label={
          isDeletingCart
            ? (labels?.deleting ?? 'Deleting...')
            : (labels?.deleteCart ?? 'Delete Cart')
        }
        disabled={isDeletingCart}
        iconLeft={{ category: 'global', name: 'trash' }}
      />
      <Button
        theme={invertedTheme}
        size="small"
        type="secondary"
        color="neutral"
        dataTestId="configure-new-vehicle-button"
        gaComponent="order-builder-checkout"
        onClick={onConfigureNewVehicle}
        label={labels?.configureNewVehicle ?? 'Configure New Vehicle'}
        iconLeft={{ category: 'global', name: 'plus' }}
      />
      <Button
        theme={invertedTheme}
        size="small"
        type="primary"
        color="neutral"
        dataTestId="add-available-vehicle-button"
        gaComponent="order-builder-checkout"
        onClick={onAddAvailableVehicle}
        label={labels?.addAvailableVehicle ?? 'Add Available Vehicle'}
        iconLeft={{ category: 'global', name: 'plus' }}
      />
    </Box>
  </Box>
);

export default CartToolbar;
